import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ChevronDown, Play, PhoneCall, MessageCircle } from 'lucide-react';

const KentAcademyHomepage = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [coursesOpen, setCoursesOpen] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const laserVideoRef = useRef(null);
  const tattooVideoRef = useRef(null);
  const nailVideoRef = useRef(null);

  const toggleExpand = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  // Auto-play videos when they come into view
  useEffect(() => {
    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.play();
        } else {
          entry.target.pause();
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.3,
    });

    if (laserVideoRef.current) observer.observe(laserVideoRef.current);
    if (tattooVideoRef.current) observer.observe(tattooVideoRef.current);
    if (nailVideoRef.current) observer.observe(nailVideoRef.current);

    return () => {
      if (laserVideoRef.current) observer.unobserve(laserVideoRef.current);
      if (tattooVideoRef.current) observer.unobserve(tattooVideoRef.current);
      if (nailVideoRef.current) observer.unobserve(nailVideoRef.current);
    };
  }, []);

  const courses = [
    {
      title: 'Level 4 in Laser and Light',
      price: '£1,495',
      image: '/assets/Laser2.JPG',
      description: 'Level 4 accredited training with hands-on practice and the latest laser equipment. Learn from industry professionals with over 30 years of experience.',
      highlights: [
        'Taught by Industry Professionals',
        'Accredited Level 4 Curriculum', 
        'Learn on Cutting-Edge Technology',
        'Flexible Training Options',
        'Hands-On, Real-World Skills'
      ],
      contact: (
        <p className="text-sm text-gray-700 mt-2">
          📧 <a href="mailto:admin@kentacademy.co.uk" className="text-blue-600 underline">admin@kentacademy.co.uk</a>
        </p>
      )
    },
    {
      title: 'Level 5 in Tattoo Removal (CPD available)',
      price: 'CPD & Level 5',
      image: '/assets/tattoo.jpg',
      description: 'Master cosmetic and body tattoo removal from eyebrows to full body designs. Accredited by Focus Awards with over 30 years of industry expertise.',
      highlights: [
        'Remove All Tattoo Types',
        'CPD Short Courses Available',
        'Level 5 Advanced Qualification',
        'Flexible Learning Options',
        'Accredited by Focus Awards'
      ],
      contact: (
        <p className="text-sm text-gray-700 mt-2">
          📧 <a href="mailto:admin@kentacademy.co.uk" className="text-blue-600 underline">admin@kentacademy.co.uk</a>
        </p>
      )
    },
    {
      title: 'Level 3 in Nail Technology',
      price: 'Bilingual Training',
      image: '/assets/Nails1.JPG',
      description: 'Fast-track bilingual (Vietnamese/English) courses for experienced nail workers. Meet UK council requirements and gain official qualifications.',
      highlights: [
        'Vietnamese-English Speaking Courses',
        'Experienced Vietnamese Tutors',
        'Fast-Tracked Content',
        'Meet Council Requirements',
        'Support to Open Your Own Salon'
      ],
      contact: (
        <div className="text-sm text-gray-700 mt-2 space-y-2">
          <p>📞 07735906672</p>
          <div className="flex gap-3">
            <a
              href="tel:07475935363"
              className="inline-flex items-center gap-2 border border-gray-400 text-gray-800 px-3 py-1 rounded hover:bg-gray-800 hover:text-white transition"
            >
              <PhoneCall size={16} /> Call Now
            </a>
            <a
              href="https://wa.me/447475935363"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-gray-400 text-gray-800 px-3 py-1 rounded hover:bg-gray-800 hover:text-white transition"
            >
              <MessageCircle size={16} /> WhatsApp
            </a>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="bg-[#f7f6f3] text-gray-800 font-sans">
      {/* Header Bar */}
      <div className="bg-[#333] text-white text-sm py-2 px-4 flex flex-col md:flex-row md:justify-between items-center gap-2 text-center">
        <div className="flex flex-col md:flex-row items-center gap-2">
          <a href="mailto:admin@kentacademy.co.uk" className="hover:underline">
            📧 admin@kentacademy.co.uk
          </a>
        </div>
        <a
          href="https://www.facebook.com/share/16qS8YJbJc/?mibextid=wwXIfr"
          target="_blank"
          rel="noopener noreferrer"
          className="text-white hover:underline flex items-center gap-1"
        >
          <img src="/assets/facebook-icon.png" alt="Facebook" className="w-5 h-5" />
          facebook.com/KentAcademy
        </a>
      </div>

      {/* Navigation */}
      <nav className="bg-white shadow-sm w-full z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 pt-6 pb-4 flex items-center justify-center">
        <Link to="/" className="cursor-pointer">
  <img
    src="/assets/KaLogo.png"
    alt="Kent Academy"
    className="h-40 w-auto object-contain"
  />
</Link>

        </div>
        
        {/* Desktop Nav */}
        <div className="bg-[#f7f6f3] border-t border-gray-200 hidden md:flex">
          <div className="max-w-4xl mx-auto px-4 flex justify-between items-center text-sm font-medium py-3 relative w-full">
            <Link to="/" className="text-gray-800 hover:text-black transition-colors duration-200">HOME</Link>
            <Link to="/about" className="text-gray-800 hover:text-black transition-colors duration-200">ABOUT US</Link>
            
            <div
              className="relative cursor-pointer group"
              onMouseEnter={() => setCoursesOpen(true)}
              onMouseLeave={() => setCoursesOpen(false)}
            >
              <div className="flex items-center gap-1 text-gray-800 hover:text-black transition-colors duration-200 cursor-pointer">
                <span>ALL COURSES</span>
                <ChevronDown 
                  size={16} 
                  className={`transition-transform duration-200 ${coursesOpen ? 'rotate-180' : ''}`}
                />
              </div>
              
              {/* Dropdown */}
        <div className={`absolute top-full left-1/2 transform -translate-x-1/2 mt-1 transition-all duration-200 z-50 ${
          coursesOpen 
            ? 'opacity-100 visible translate-y-0' 
            : 'opacity-0 invisible -translate-y-2'
        }`}>
          <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-3 h-3 bg-white border-l border-t border-gray-200 rotate-45"></div>
          
          <div className="bg-white border border-gray-200 rounded-md shadow-lg w-64 overflow-hidden">
            <div className="py-1">
              {/* Level 3 first */}
              <Link 
                to="/courses/vietnamese-nails" 
                className="block px-4 py-2 hover:bg-gray-50 transition-colors duration-150"
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-pink-600 rounded-full"></div>
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">Level 3 in Nail Technology</h4>
                    <p className="text-xs text-gray-600">Bilingual training for UK professionals</p>
                  </div>
                </div>
              </Link>

              <Link 
                to="/courses/laser-and-light" 
                className="block px-4 py-2 hover:bg-gray-50 transition-colors duration-150"
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">Level 4 in Laser and Light</h4>
                    <p className="text-xs text-gray-600">Professional laser training</p>
                  </div>
                </div>
              </Link>

              <Link 
                to="/courses/tattoo-removal" 
                className="block px-4 py-2 hover:bg-gray-50 transition-colors duration-150"
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">Level 5 in Tattoo Removal</h4>
                    <p className="text-xs text-gray-600">CPD Available</p>
                  </div>
                </div>
              </Link>
            </div>

            {/* Optional: keep Services overview */}
            <div className="border-t border-gray-100 px-2 py-2">
              <Link 
                to="/services" 
                className="block w-full text-center py-1 px-3 bg-gray-800 text-white rounded text-xs font-medium hover:bg-black transition-colors duration-200"
              >
                View All Courses
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      <Link to="/contact" className="text-gray-800 hover:text-black transition-colors duration-200">CONTACT</Link>
    </div>
  </div>

  {/* Mobile Dropdown Toggle */}
  <div className="md:hidden flex justify-center pb-3">
    <button
      onClick={() => setMobileOpen(!mobileOpen)}
      className="text-sm border px-4 py-2 rounded text-gray-800 border-gray-600 hover:bg-gray-100 transition-colors duration-200"
    >
      {mobileOpen ? 'Close Menu' : 'Menu'}
    </button>
  </div>

  {/* Mobile Dropdown Nav */}
  {mobileOpen && (
    <div className="md:hidden bg-[#f7f6f3] border-t border-gray-200 px-6 py-4 flex flex-col items-center gap-3 text-sm font-medium">
      <Link to="/" onClick={() => setMobileOpen(false)} className="text-gray-800 hover:text-black transition-colors duration-200">HOME</Link>
      <Link to="/about" onClick={() => setMobileOpen(false)} className="text-gray-800 hover:text-black transition-colors duration-200">ABOUT US</Link>
      <div className="text-gray-800 font-medium">COURSES:</div>
      {/* Level 3 first */}
      <Link to="/courses/vietnamese-nails" onClick={() => setMobileOpen(false)} className="text-gray-700 hover:text-black transition-colors duration-200 pl-4">Level 3 in Nail Technology</Link>
      <Link to="/courses/laser-and-light" onClick={() => setMobileOpen(false)} className="text-gray-700 hover:text-black transition-colors duration-200 pl-4">Level 4 in Laser and Light</Link>
      <Link to="/courses/tattoo-removal" onClick={() => setMobileOpen(false)} className="text-gray-700 hover:text-black transition-colors duration-200 pl-4">Level 5 in Tattoo Removal</Link>
      <Link to="/contact" onClick={() => setMobileOpen(false)} className="text-gray-800 hover:text-black transition-colors duration-200">CONTACT</Link>
      <a
        href="mailto:admin@kentacademy.co.uk"
        className="inline-block border border-gray-800 text-gray-800 px-3 py-1 rounded hover:bg-gray-800 hover:text-white transition-colors duration-200"
      >
        Contact Us
      </a>
    </div>
  )}
</nav>
      {/* Hero Section - This is where the hero image should be */}
      <section className="relative min-h-[500px] md:min-h-[600px] flex items-center justify-center overflow-hidden">
        <img
          src="/assets/Nails6.JPG"
          alt="Kent Academy Training"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
        <div className="absolute inset-0 bg-black/40 z-5"></div>
        <div className="relative z-10 text-center max-w-4xl px-4">
          <h1 className="text-4xl md:text-6xl font-light mb-6 text-white">
            YOUR FUTURE IN <span className="font-semibold">BEAUTY, STARTS HERE</span>
          </h1>
          <p className="text-lg md:text-xl mb-8 text-white/90 max-w-2xl mx-auto">
            Accredited training in advanced beauty and nail technology delivered by experts.
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="relative order-2 md:order-1">
            <img 
              src="/assets/Laser2.JPG" 
              alt="Professional Beauty Training" 
              className="w-full h-80 object-cover rounded-lg shadow-lg"
            />
          </div>
          <div className="order-1 md:order-2">
            <h2 className="text-3xl md:text-4xl font-light mb-6">
              Approved courses aimed at those looking for <span className="font-semibold">careers within the beauty industry</span>
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Kent Academy was established to provide world-class beauty training with over 30 years of hands-on experience in aesthetics and laser technology. We design and manufacture our own beauty machines, using the latest technology to give you a competitive edge from day one.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Our flexible training options mean you can learn one-on-one or in groups, at our fully equipped academy or in your own salon. We bring the training to suit your style and schedule.
            </p>
          </div>
        </div>
      </section>

      {/* Vietnamese Nail Training Section - MOVED TO FIRST */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-light mb-6">
                Level 3 <span className="font-semibold"> In Nail Technology</span>
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Fast-track bilingual training designed specifically for Vietnamese-speaking professionals working in the UK. Our experienced Vietnamese tutors provide comprehensive support to help you gain official qualifications and meet local council requirements.
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-pink-600 rounded-full mt-2"></div>
                  <p className="text-gray-700">Vietnamese-English speaking courses with expert tutors</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-pink-600 rounded-full mt-2"></div>
                  <p className="text-gray-700">Fast-tracked content covering theory and practical skills</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-pink-600 rounded-full mt-2"></div>
                  <p className="text-gray-700">Council-compliant certification for UK requirements</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-pink-600 rounded-full mt-2"></div>
                  <p className="text-gray-700">Flexible training options to suit your schedule</p>
                </div>
              </div>
              <Link to="/courses/vietnamese-nails" className="inline-block bg-gray-800 text-white px-6 py-3 rounded hover:bg-black transition-colors duration-200">
                Learn More About Nail Training
              </Link>
            </div>
            <div className="relative order-2 md:order-1">
              <video 
                ref={nailVideoRef}
                width="100%" 
                height="400"
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-80 object-cover rounded-lg shadow-lg"
                poster="/assets/Nails1.JPG"
              >
                <source src="/assets/Nailvideo.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Laser Training Section - MOVED TO SECOND */}
      <section className="py-16 bg-[#f7f6f3]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 md:order-1">
              <video 
                ref={laserVideoRef}
                width="100%" 
                height="400"
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-80 object-cover rounded-lg shadow-lg"
                poster="/assets/Laser2.JPG"
              >
                <source src="/assets/Laser.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-3xl md:text-4xl font-light mb-6">
                Master <span className="font-semibold">Laser & IPL Technology</span>
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Step into the world of professional aesthetics with our Level 4 Laser Hair Removal Course. Taught by industry professionals with over 30 years of hands-on experience, you'll learn on cutting-edge technology that we design and manufacture ourselves.
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <p className="text-gray-700">Learn on cutting-edge laser and IPL technology</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <p className="text-gray-700">Accredited Level 4 curriculum with national standards</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <p className="text-gray-700">Hands-on practice with live models and real-world skills</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <p className="text-gray-700">Flexible training options to suit your schedule</p>
                </div>
              </div>
              <Link to="/courses/laser-and-light" className="inline-block bg-gray-800 text-white px-6 py-3 rounded hover:bg-black transition-colors duration-200">
                Learn More About Laser Training
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Tattoo Removal Section - MOVED TO THIRD */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-light mb-6">
                Advanced <span className="font-semibold">Tattoo Removal Training</span>
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Master cosmetic and body tattoo removal with our comprehensive training courses. From eyebrow tattoo removal to complex coloured and black ink designs, we provide expert-led, accredited education that prepares you for real-world results.
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                  <p className="text-gray-700">Remove all tattoo types - cosmetic to full body designs</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                  <p className="text-gray-700">CPD Short Courses and Level 5 advanced qualifications</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                  <p className="text-gray-700">Accredited by Focus Awards with industry expertise</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                  <p className="text-gray-700">Flexible learning at our academy or your salon</p>
                </div>
              </div>
              <Link to="/courses/tattoo-removal" className="inline-block bg-gray-800 text-white px-6 py-3 rounded hover:bg-black transition-colors duration-200">
                Discover Tattoo Removal Training
              </Link>
            </div>
            <div className="relative order-2 md:order-1">
              <video 
                ref={tattooVideoRef}
                width="100%" 
                height="400"
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-80 object-cover rounded-lg shadow-lg"
                poster="/assets/tattoo.jpg"
              >
                <source src="/assets/Tattoo.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Our Top Courses Section */}
      <section id="courses" className="py-16 bg-[#f7f6f3]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-light text-center mb-4">
            OUR TOP <span className="font-semibold">COURSES</span>
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Here at Kent Academy, we provide various courses at various levels for you to choose from. Because we believe everyone is an individual. You decide how to enhance your skills and knowledge about advanced beauty and nail technology.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course, i) => (
              <div key={i} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="relative h-48">
                  <img src={course.image} alt={course.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">{course.title}</h3>
                  <p className="text-blue-600 font-medium mb-3">{course.price}</p>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">{course.description}</p>
                  
                  <button
                    onClick={() => toggleExpand(i)}
                    className="w-full bg-gray-800 text-white py-2 px-4 rounded hover:bg-black transition-colors duration-200 mb-4"
                  >
                    {expandedIndex === i ? 'HIDE DETAILS' : 'READ MORE →'}
                  </button>
                  
                  {expandedIndex === i && (
                    <div className="space-y-3 pt-4 border-t border-gray-100">
                      <h4 className="font-semibold text-gray-900">Key Features:</h4>
                      <ul className="space-y-1">
                        {course.highlights.map((highlight, index) => (
                          <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-blue-600 mt-1">•</span>
                            {highlight}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div className="mt-4">
                    {course.contact}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-light text-center mb-12">
            What Our <span className="font-semibold">Students Say</span>
          </h2>
          <div className="w-full max-w-xl mx-auto">
            <img
              src="/assets/testimonial1.jpeg"
              alt="Student testimonial from Linh"
              className="rounded-xl shadow-md w-full h-auto object-contain"
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#333] text-white py-12">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-8 text-sm">
          <div>
            <img src="/assets/KaLogo.png" alt="Kent Academy" className="w-14 mb-3" />
            <p className="text-gray-300">
              Kent Academy delivers advanced beauty training with professional-grade equipment and expert guidance.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-2">Courses</h4>
            <ul className="space-y-1 text-gray-400">
              <li>Level 4 in Laser and Light</li>
              <li>Level 5 in Tattoo Removal</li>
              <li>Level 3 in Nail Technology</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">Contact</h4>
            <ul className="space-y-1 text-gray-400">
              <li>
                <a href="mailto:admin@kentacademy.co.uk" className="hover:text-gray-300 transition-colors duration-200">
                  admin@kentacademy.co.uk
                </a>
              </li>
              <li>General: 07548321022</li>
              <li>Vietnamese Courses: 07548321022</li>
              <li>Unit 10, Creative Quarter, Javelin Way, Ashford TN24 8FN</li>
              <li>
                <a
                  href="https://www.facebook.com/share/16qS8YJbJc/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-white hover:underline"
                >
                  <img src="/assets/facebook-icon.png" alt="Facebook" className="w-6 h-6" />
                  facebook.com/KentAcademy
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-10 text-center text-xs text-gray-400">© 2025 Kent Academy. All rights reserved.</div>
      </footer>
    </div>
  );
};

export default KentAcademyHomepage;